import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  Sparkles,
  Briefcase,
  Rocket,
  MapPin,
  Heart,
  Loader2,
  Wand2,
} from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import { apiClient, getSessionId, getBirthDetails, getProfileKey } from "@/lib/api";
import { track } from "@/lib/analytics";
import Sparks from "@/components/Sparks";
import { useLang } from "@/lib/i18n";

const PATHS = ["conservative", "balanced", "bold"];
const RISK_COLOR = { Low: "#34d399", Medium: "#D4AF37", High: "#f97316" };

function ScoreBar({ label, value, valueLabel }) {
  const pct = Math.min(100, Math.max(0, value || 0));
  return (
    <div>
      <div className="flex justify-between items-baseline mb-1.5">
        <span className="text-[10px] tracking-[0.2em] uppercase text-slate-400">{label}</span>
        <span className="text-sm font-serif text-white">{pct}</span>
      </div>
      <div className="h-1.5 w-full bg-white/8 rounded-full overflow-hidden">
        <motion.div
          className="h-full rounded-full"
          style={{ background: "#D4AF37" }}
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.9, ease: "easeOut" }}
        />
      </div>
      {valueLabel && (
        <div className="mt-1 text-xs text-slate-300 font-light">{valueLabel}</div>
      )}
    </div>
  );
}

function ScenarioCard({ data, accent, testId, t }) {
  const localizedRisk =
    data.risk_level === "Low" ? t("sim.risk.low")
      : data.risk_level === "High" ? t("sim.risk.high")
      : t("sim.risk.medium");
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="glass rounded-3xl p-7 flex flex-col"
      data-testid={testId}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] tracking-[0.25em] uppercase" style={{ color: accent }}>
          {data.title}
        </div>
        <div
          className="text-[10px] tracking-[0.2em] uppercase px-2 py-0.5 rounded-full"
          style={{
            background: `${RISK_COLOR[data.risk_level] || "#94a3b8"}20`,
            color: RISK_COLOR[data.risk_level] || "#94a3b8",
          }}
        >
          {t("sim.riskLabel", { level: localizedRisk })}
        </div>
      </div>
      <h3 className="font-serif text-2xl text-white leading-snug">{data.subtitle}</h3>
      <p className="mt-4 text-sm text-slate-300 font-light leading-relaxed">{data.narrative}</p>

      <div className="mt-6 mb-6">
        <div className="text-[10px] tracking-[0.25em] uppercase gold-text mb-2">
          {t("sim.probability")}
        </div>
        <div className="flex items-baseline gap-2">
          <div className="font-serif text-5xl text-white">{data.probability_of_satisfaction}</div>
          <div className="text-sm text-slate-400">{t("sim.of100")}</div>
        </div>
      </div>

      <div className="space-y-4 mb-6">
        <ScoreBar label={t("sim.impact.emotional")} value={data.emotional_impact_score} valueLabel={data.emotional_impact_label} />
        <ScoreBar label={t("sim.impact.financial")} value={data.financial_impact_score} valueLabel={data.financial_impact_label} />
        <ScoreBar label={t("sim.impact.relationship")} value={data.relationship_impact_score} valueLabel={data.relationship_impact_label} />
      </div>

      <div className="mt-auto pt-5 border-t border-white/8">
        <div className="text-[10px] tracking-[0.25em] uppercase gold-text mb-2">{t("sim.recommended")}</div>
        <p className="text-sm text-slate-100 font-light leading-relaxed">{data.recommended_action}</p>
      </div>
    </motion.div>
  );
}

export default function Simulator() {
  const navigate = useNavigate();
  const { t } = useLang();
  const [question, setQuestion] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const SUGGESTIONS = [
    { icon: Briefcase, text: t("sim.suggest.job") },
    { icon: Rocket, text: t("sim.suggest.business") },
    { icon: MapPin, text: t("sim.suggest.move") },
    { icon: Heart, text: t("sim.suggest.marry") },
  ];

  const run = async (q) => {
    const text = (q ?? question).trim();
    if (!text || text.length < 8) {
      toast.error(t("sim.err.short"));
      return;
    }
    setQuestion(text);
    setLoading(true);
    setResult(null);
    try {
      const sid = getSessionId();
      const birth = getBirthDetails();
      const pk = getProfileKey();
      const res = await apiClient.post(
        "/simulator",
        { session_id: sid, question: text, birth, profile_key: pk || null },
        { timeout: 90000 }
      );
      setResult(res.data);
      track("simulation_run", {
        properties: { question: text, simulation_id: res.data.id },
      });
    } catch (e) {
      console.error(e);
      toast.error(t("sim.err.fail"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen text-slate-100">
      <Navbar />
      <main className="cosmic-bg">
        <Sparks />
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-20 pb-24" data-testid="simulator-page">
          <div
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-[10px] tracking-[0.3em] uppercase mb-6"
            style={{
              background: "rgba(212,175,55,0.08)",
              border: "1px solid rgba(212,175,55,0.3)",
              color: "#D4AF37",
            }}
          >
            <Wand2 className="w-3 h-3" />
            {t("sim.eyebrow")}
          </div>
          <h1 className="font-serif text-5xl md:text-6xl font-light text-white leading-[1.05] tracking-tight">
            {t("sim.title.a")}<br />{t("sim.title.b")} <span className="italic gold-text">{t("sim.title.italic")}</span>
          </h1>
          <p className="mt-6 text-lg text-slate-300/90 max-w-2xl font-light">
            {t("sim.subtitle")}
          </p>

          {/* Input */}
          <div className="mt-12 max-w-3xl">
            <div className="glass rounded-3xl p-3 flex items-center gap-2">
              <input
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && run()}
                placeholder={t("sim.placeholder")}
                className="flex-1 bg-transparent outline-none text-white text-base placeholder:text-slate-500 px-4 py-3"
                data-testid="simulator-input"
              />
              <button
                onClick={() => run()}
                disabled={loading}
                className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-medium disabled:opacity-60"
                style={{ background: "#D4AF37", color: "#0B1020" }}
                data-testid="simulator-run-button"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {loading ? t("sim.button.simulating") : t("sim.button.simulate")}
              </button>
            </div>
            <div className="mt-5 flex flex-wrap gap-2">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s.text}
                  onClick={() => run(s.text)}
                  disabled={loading}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm border border-white/10 bg-white/5 hover:border-[#D4AF37]/40 text-slate-200 transition-colors disabled:opacity-60"
                  data-testid={`suggestion-${s.text.toLowerCase().replace(/[^a-z]+/g, "-").slice(0, 30)}`}
                >
                  <s.icon className="w-3.5 h-3.5 gold-text" strokeWidth={1.5} />
                  {s.text}
                </button>
              ))}
            </div>
          </div>

          {/* Loading state */}
          <AnimatePresence>
            {loading && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6"
                data-testid="simulator-loading"
              >
                {[0, 1, 2].map((i) => (
                  <div key={i} className="glass rounded-3xl p-7 h-72 animate-pulse" />
                ))}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Results */}
          {result && (
            <div className="mt-16" data-testid="simulator-results">
              <div className="mb-8">
                <div className="text-[10px] tracking-[0.25em] uppercase gold-text mb-2">
                  {t("sim.yourQuestion")}
                </div>
                <h2 className="font-serif text-2xl md:text-3xl text-white leading-snug">
                  {result.question}
                </h2>
                {result.result?.summary && (
                  <p className="mt-3 text-slate-400 font-light max-w-2xl">{result.result.summary}</p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {PATHS.map((key, i) => (
                  <ScenarioCard
                    key={key}
                    accent={i === 0 ? "#94a3b8" : i === 1 ? "#D4AF37" : "#f97316"}
                    data={result.result.scenarios[key]}
                    testId={`scenario-${key}`}
                    t={t}
                  />
                ))}
              </div>

              {result.result?.mantra_take && (
                <div className="mt-10 glass rounded-3xl p-10 gold-glow" data-testid="simulator-mantra-take">
                  <div className="text-[10px] tracking-[0.25em] uppercase gold-text mb-3">{t("sim.mantraTake")}</div>
                  <p className="font-serif text-2xl md:text-3xl font-light text-white leading-snug">
                    {result.result.mantra_take}
                  </p>
                </div>
              )}

              <div className="mt-12 flex flex-wrap gap-4">
                <button
                  onClick={() => navigate("/onboarding")}
                  className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-medium gold-glow"
                  style={{ background: "#D4AF37", color: "#0B1020" }}
                  data-testid="simulator-get-reading-button"
                >
                  {t("sim.getReading")} <ArrowRight className="w-4 h-4" />
                </button>
                <button
                  onClick={() => {
                    setResult(null);
                    setQuestion("");
                  }}
                  className="rounded-full px-7 py-3.5 text-sm font-medium border border-white/20 hover:bg-white/5"
                  data-testid="simulator-reset-button"
                >
                  {t("sim.askAnother")}
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
